import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import "../styles/HeroSection.css";
import StatsBar from "./StatsBar";

import heroImg from "../assets/home-hero.jpg";

/* ─────────────────────────────────────────────
   WhatsApp number — same as Navbar contact number
   (73587 98182) prefixed with India country code

   Rendered as a real <a href> (not window.open) —
   browsers reliably allow genuine anchor clicks to
   open a new tab, while script-triggered popups are
   frequently blocked.
───────────────────────────────────────────── */
const WHATSAPP_NUMBER = "917358798182";

function buildWhatsAppUrl(message) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message
  )}`;
}

const BOOK_APPOINTMENT_MESSAGE =
  "Hi, I'd like to book an appointment at TANBIO Biomed Genomics Laboratory. Please share the available slots.";

/* Route + section id that "Explore Tests" should land on —
   same target used by the Health Packages / Popular Tests
   "View All Tests" actions. */
const EXPLORE_TESTS_ROUTE = "/service";
const EXPLORE_TESTS_TARGET_ID = "diagnosticservices";

function scrollToId(id, retries = 10) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (retries > 0) {
    setTimeout(() => scrollToId(id, retries - 1), 80);
  }
}

/* ─── Feature Badge ──────────────────────────────── */
const FeatureBadge = ({ icon, title, sub }) => (
  <div className="hero-feature-badge">
    <div className="hero-feature-icon">
      <i className={icon}></i>
    </div>
    <div>
      <div className="hero-feature-title">{title}</div>
      <div className="hero-feature-sub">{sub}</div>
    </div>
  </div>
);

/* ─── HeroSection ────────────────────────────────── */
const HeroSection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  function handleExploreTests() {
    if (location.pathname === EXPLORE_TESTS_ROUTE) {
      scrollToId(EXPLORE_TESTS_TARGET_ID);
    } else {
      navigate(EXPLORE_TESTS_ROUTE);
      setTimeout(() => scrollToId(EXPLORE_TESTS_TARGET_ID), 120);
    }
  }

  return (
    <section className="hero-section">

      {/* RIGHT BG PANEL */}
      <div className="hero-right-bg">
        <span className="hero-right-border-accent" aria-hidden="true" />
        <div className="hero-image-wrapper">
          <img
            src={heroImg}
            alt="Laboratory hero"
            loading="lazy"
            className="hero-right-pattern"
          />
        </div>
      </div>

      {/* LEFT CONTENT */}
      <Container className="hero-container">
        <Row className="hero-inner align-items-center">
          <Col xs={12} lg={6} className="hero-left-col">

            <div className="hero-anim-text">

              <h1 className="hero-heading">
                Accurate Results,<br />
                <span className="hero-heading-highlight">Better Health</span>
              </h1>

              <p className="hero-desc">
                TANBIO Biomed Genomics Laboratory provides advanced
                diagnostic services with precision, care and reliability.
              </p>

              <div className="hero-features mb-4">
                <FeatureBadge
                  icon="fa-solid fa-bullseye"
                  title="Accurate"
                  sub="Results"
                />
                <FeatureBadge
                  icon="fa-solid fa-clock"
                  title="Quick"
                  sub="Turnaround"
                />
                <FeatureBadge
                  icon="fa-solid fa-shield-halved"
                  title="Safe &"
                  sub="Reliable"
                />
              </div>

              {/* Button group — controlled by hero-btn-group in CSS */}
              <div className="hero-btn-group">
                <Button
                  as="a"
                  href={buildWhatsAppUrl(BOOK_APPOINTMENT_MESSAGE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-hero-primary"
                >
                  <i className="fa-brands fa-whatsapp me-2"></i>
                  Book Appointment
                </Button>
                <Button
                  type="button"
                  onClick={handleExploreTests}
                  className="btn-hero-outline"
                >
                  <i className="fa-solid fa-flask me-2"></i>
                  Explore Tests
                </Button>
              </div>

            </div>

          </Col>
        </Row>
      </Container>

      {/* STATS BAR */}
      <StatsBar />

    </section>
  );
};

export default HeroSection;